import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DOCUMENTOS_EXT_PERMITIDAS,
  DOCUMENTO_ESTADOS,
  documentoLabel,
} from "../constants/documentos";
import { listarDocumentosCatalogo } from "../../../../services/documentosCatalogoService";

const OCR_DECISION_CONFIG = {
  aprobado:  { cls: "bg-success",          label: "OCR OK"       },
  rechazado: { cls: "bg-danger",           label: "OCR Rechazado" },
  pendiente: { cls: "bg-warning text-dark", label: "OCR Pendiente" },
};

const FLAG_LABELS = {
  // Críticos
  id_checksum_invalid:       "El número de cédula tiene un dígito verificador inválido",
  id_profile_mismatch:       "La cédula detectada no coincide con la del estudiante",
  missing_front_or_back:     "Falta la cara frontal o posterior del documento",
  exam_result_positive:      "Resultado positivo en examen toxicológico",
  exam_result_no_apt:        "Resultado no apto en examen psicológico",
  // Mayores
  id_format_invalid:         "El número detectado no tiene el formato de una cédula ecuatoriana",
  id_document_context_low:   "El documento no contiene elementos visuales de una cédula ecuatoriana",
  document_type_mismatch:    "El archivo no corresponde al tipo de documento esperado",
  name_profile_mismatch:     "El nombre en el documento no coincide con el perfil del estudiante",
  document_expired:          "El documento está vencido",
  license_number_not_found:  "No se encontró el número de licencia",
  license_id_mismatch:       "El número de licencia no coincide con el estudiante",
  categories_not_found:      "No se encontraron las categorías de la licencia",
  exam_result_not_found:     "No se encontró el resultado del examen",
  certificate_context_low:   "El certificado no tiene los elementos visuales esperados",
  subject_id_unverified:     "No se pudo leer el número de cédula del documento",
  // Menores
  image_quality_low:         "La imagen tiene baja calidad o resolución insuficiente para análisis",
  ocr_low_confidence:        "El texto extraído tiene baja confianza (imagen poco legible)",
  possible_crop_or_cut:      "El documento parece estar recortado o con bordes cortados",
  document_expiring_soon:    "El documento vence pronto (en menos de 30 días)",
  nui_corrected_by_ocr:      "El número de cédula fue corregido automáticamente",
  name_score_low:            "El nombre tiene baja similitud con el perfil del estudiante",
  license_has_restriction:   "La licencia tiene restricciones activas",
  military_number_not_found: "Número militar no encontrado (suele estar al reverso)",
  blood_type_not_found:      "Tipo de sangre no legible en el documento",
  // Informativo
  multi_doc_upload_hint:     "El PDF contiene varios documentos. Se recomienda subir por separado",
  analysis_failed:           "El análisis no pudo completarse",
};

const FIELD_LABELS = {
  nui:                "Cédula",
  nombres:            "Nombres",
  apellidos:          "Apellidos",
  fecha_nacimiento:   "Fecha de nacimiento",
  sexo:               "Sexo",
  tipo_sangre:        "Tipo de sangre",
  fecha_emision:      "Fecha de emisión",
  fecha_vencimiento:  "Fecha de vencimiento",
  provincia_emision:  "Provincia de emisión",
  resultado:          "Resultado",
  numero_certificado: "Nº certificado",
  numero_licencia:    "Nº licencia",
  categorias:         "Categorías",
  restricciones:      "Restricciones",
  tipo_examen:        "Tipo de examen",
  fecha:              "Fecha",
};

const REASON_LABELS = {
  id_checksum_invalid:                    "La cédula tiene dígito verificador inválido",
  id_profile_mismatch:                    "La cédula no coincide con la del estudiante",
  id_format_invalid:                      "Formato de cédula inválido",
  document_type_mismatch:                 "El archivo no es el tipo de documento esperado",
  score_high_risk:                        "El puntaje de confianza es demasiado bajo para aprobación automática",
  score_low_risk_and_no_critical_flags:   "Puntaje alto y sin alertas críticas",
  manual_review_required:                 "Requiere revisión manual (puntaje medio)",
};

function OcrDetalleModal({ doc, onClose, onRetry, retrying, onLoad }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    onLoad(doc.id).then((res) => {
      if (!cancelled) { setDetalle(res); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [doc.id, onLoad]);

  const analisis  = detalle?.analisis || null;
  const flags     = Array.isArray(analisis?.flags_json) ? analisis.flags_json : [];
  const motivos   = Array.isArray(detalle?.motivos_decision) ? detalle.motivos_decision : [];
  const score     = analisis?.score_confianza   ?? doc.analisisScore    ?? null;
  const decision  = analisis?.decision_automatica || detalle?.decision_automatica || doc.decisionAnalisis || null;
  const riesgo    = analisis?.nivel_riesgo      || doc.analisisRiesgo   || null;
  const fields    = analisis?.fields_extracted  || null;
  const cfg       = OCR_DECISION_CONFIG[decision];
  const sinDatos  = !loading && !analisis;

  const todasLasAlertas = Array.from(new Set([...motivos, ...flags]))
    .filter((c) => !["score_low_risk_and_no_critical_flags", "multi_doc_upload_hint"].includes(c));

  const fieldEntries = fields
    ? Object.entries(fields).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  const CRITICAL_FLAGS_SET = new Set([
    "id_checksum_invalid", "id_profile_mismatch", "document_type_mismatch", "score_high_risk",
    "missing_front_or_back", "exam_result_positive", "exam_result_no_apt",
  ]);

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} aria-modal="true" role="dialog">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
        <div className="modal-content">
          <div className="modal-header border-0 pb-1">
            <div>
              <h6 className="modal-title mb-0">Resultado del análisis</h6>
              <small className="text-muted">{doc.nombre || doc.tipo}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body pt-2" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
            ) : sinDatos ? (
              <p className="text-muted small mb-0">Este documento aún no tiene un análisis registrado. Usa el botón Reanalizar.</p>
            ) : (
              <>
                {/* Decisión + score */}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                  <span className={`badge ${cfg?.cls || "bg-secondary"}`}>{cfg?.label || "Sin análisis"}</span>
                  {riesgo && (
                    <span className={`badge border ${riesgo === "alto" ? "text-danger border-danger" : riesgo === "medio" ? "text-warning border-warning" : "text-success border-success"} bg-transparent`}>
                      Riesgo {riesgo}
                    </span>
                  )}
                  {score !== null && (
                    <span className="badge bg-light text-dark border">Puntaje: {score}/100</span>
                  )}
                </div>

                {/* Datos leídos del documento */}
                {fieldEntries.length > 0 && (
                  <div className="mb-3">
                    <p className="small fw-semibold text-muted mb-1" style={{ textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.05em" }}>
                      Datos leídos del documento
                    </p>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.8rem" }}>
                      <tbody>
                        {fieldEntries.map(([key, value]) => (
                          <tr key={key}>
                            <td className="text-muted fw-medium" style={{ width: "40%" }}>
                              {FIELD_LABELS[key] || key}
                            </td>
                            <td>
                              {Array.isArray(value) ? value.join(", ") : String(value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Flags / observaciones */}
                {todasLasAlertas.length > 0 ? (
                  <>
                    <p className="small fw-semibold mb-2">
                      {decision === "rechazado" ? "Motivos del rechazo:" : "Observaciones detectadas:"}
                    </p>
                    <ul className="list-unstyled mb-0">
                      {todasLasAlertas.map((code) => {
                        const label = REASON_LABELS[code] || FLAG_LABELS[code] || code;
                        const esCritico = CRITICAL_FLAGS_SET.has(code);
                        return (
                          <li key={code} className="small d-flex align-items-start gap-2 mb-2">
                            <i className={`bi ${esCritico ? "bi-x-circle-fill text-danger" : "bi-exclamation-circle text-warning"} flex-shrink-0 mt-1`} style={{ fontSize: "0.75rem" }} />
                            <span>{label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : decision === "aprobado" ? (
                  <p className="small text-success mb-0">
                    <i className="bi bi-check-circle-fill me-1" />
                    El documento pasó la verificación automática sin observaciones.
                  </p>
                ) : (
                  <p className="small text-muted mb-0">No se registraron observaciones específicas. Revisa manualmente.</p>
                )}
              </>
            )}
          </div>
          <div className="modal-footer border-0 pt-1">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={retrying}
              onClick={() => { onRetry?.(doc.id); onClose(); }}
            >
              <i className={`bi bi-arrow-clockwise me-1${retrying ? " spin" : ""}`} />
              Reanalizar
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OcrBadge({ doc, onRetry, onVerDetalle, retrying }) {
  if (!doc) return null;
  const decision = doc.decisionAnalisis;
  const cfg = OCR_DECISION_CONFIG[decision];
  return (
    <div className="d-flex align-items-center gap-1">
      <button
        type="button"
        className={`badge border-0 ${cfg ? cfg.cls : "bg-secondary"}`}
        style={{ fontSize: "0.7rem", cursor: "pointer" }}
        title="Ver detalle OCR"
        onClick={() => onVerDetalle?.(doc)}
      >
        {cfg ? cfg.label : "Sin OCR"}
      </button>
      <button
        type="button"
        className="btn btn-link p-0 text-muted"
        style={{ fontSize: "0.75rem", lineHeight: 1 }}
        title="Reanalizar con OCR"
        disabled={retrying}
        onClick={() => onRetry?.(doc.id)}
      >
        <i className={`bi bi-arrow-clockwise${retrying ? " spin" : ""}`} />
      </button>
    </div>
  );
}

export default function DocumentosPanel({
  seleccionado,
  documentos,
  loadingDocs,
  docsError,
  uploading,
  handleSubir,
  handleEliminarDoc,
  getVerUrl,
  getDescargarUrl,
  handleCambiarEstado,
  handleMarcarNoAplica,
  handleRetryAnalisis,
  handleVerAnalisis,
  retryingAnalisis = false,
  canUpload = true,
  canDelete = true,
}) {
  const [docConfirm, setDocConfirm] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [ocrDetalleDoc, setOcrDetalleDoc] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const fileInputRef = useRef(null);

  // Catálogo administrable (Configuración > Documentos) en vez de la lista
  // fija DOCUMENTO_TIPOS. Se pide una sola vez; activar/desactivar/agregar
  // tipos ya no requiere tocar código.
  useEffect(() => {
    listarDocumentosCatalogo()
      .then((data) => setCatalogo(Array.isArray(data) ? data : []))
      .catch(() => setCatalogo([]));
  }, []);

  const handleVerDetalleOcr = useCallback((doc) => {
    if (handleVerAnalisis) setOcrDetalleDoc(doc);
  }, [handleVerAnalisis]);

  const docsMap = useMemo(() => {
    const map = {};
    (documentos || []).forEach((d) => {
      if (!map[d.tipo]) map[d.tipo] = [];
      map[d.tipo].push(d);
    });
    return map;
  }, [documentos]);

  // Tipos a mostrar: los activos del catálogo, más cualquiera que ya esté
  // inactivo pero tenga un documento subido para este estudiante (no
  // desaparece el historial al retirar un tipo de documento).
  const tiposDocumento = useMemo(() => {
    return catalogo
      .filter((t) => t.activo || (docsMap[t.codigo] || []).length > 0)
      .map((t) => ({ value: t.codigo, label: t.nombre, requerido: !!t.requerido, activo: !!t.activo }));
  }, [catalogo, docsMap]);

  const previewMeta = useMemo(() => {
    if (!previewDoc) return { isPdf: false, isImage: false, ext: "" };
    const name = (previewDoc.nombre || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";
    return {
      isPdf: ext === "pdf",
      isImage: ["jpg", "jpeg", "png"].includes(ext),
      ext,
    };
  }, [previewDoc]);

  const previewUrl = previewDoc ? getVerUrl(previewDoc.id) : "";
  const isModalOpen = Boolean(docConfirm || previewDoc || ocrDetalleDoc);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (isModalOpen) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [isModalOpen]);

  const triggerUpload = (tipo) => {
    setUploadTarget(tipo);
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    const ok = await handleSubir?.({ file, tipo_documento: uploadTarget, descripcion: documentoLabel(uploadTarget) });
    if (ok) setUploadTarget(null);
  };

  const tipoSangreValor = (seleccionado?.tipoSangre || seleccionado?.tipo_sangre || "").toString().trim();
  const tieneTipoSangre = Boolean(tipoSangreValor);

  const buildEstadoOptions = (doc) => {
    if (!doc) {
      return DOCUMENTO_ESTADOS.filter((opt) => ["pendiente", "no_aplica"].includes(opt.value));
    }
    if (doc.estado === "no_aplica") return DOCUMENTO_ESTADOS;
    return DOCUMENTO_ESTADOS.filter((opt) => opt.value !== "no_aplica");
  };

  return (
    <>
      {docsError && <div className="alert alert-danger">{docsError}</div>}

      {loadingDocs ? (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead className="table-light">
              <tr>
                <th>Tipo</th>
                <th>Archivo</th>
                <th style={{ width: 180 }}>Estado</th>
                <th style={{ width: 130 }}>OCR</th>
                <th className="text-end" style={{ width: 220 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {tiposDocumento.map((tipo) => {
                const docsByTipo = docsMap[tipo.value] || [];
                const doc = docsByTipo[0] || null;
                const estadoActual = doc?.estado || "pendiente";
                const isNoAplica = estadoActual === "no_aplica";
                const esCarnetSangre = tipo.value === "carnet_tipo_sangre";
                const esAntecedentes = tipo.value === "certificado_antecedentes";
                const usaTipoSangreFicha = esCarnetSangre && !doc && tieneTipoSangre;
                const canUploadDoc = canUpload && !isNoAplica && !usaTipoSangreFicha;
                const resultadoAntecedentes = doc?.resultadoAntecedentes || "";
                return (
                  <tr key={tipo.value}>
                    <td>{tipo.label}</td>
                    <td>
                      {usaTipoSangreFicha ? (
                        <span className="text-muted">Registrado en ficha</span>
                      ) : isNoAplica ? (
                        <span className="text-muted">No aplica</span>
                      ) : doc ? (
                        doc.nombre || <span className="text-muted">No subido</span>
                      ) : (
                        <span className="text-muted">No subido</span>
                      )}
                    </td>
                    <td>
                      {usaTipoSangreFicha ? (
                        <span className="badge bg-success">Registrado</span>
                      ) : doc ? (
                        <select
                          className="form-select form-select-sm"
                          value={estadoActual}
                          onChange={(e) => handleCambiarEstado?.(doc.id, e.target.value)}
                        >
                          {buildEstadoOptions(doc).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          className="form-select form-select-sm"
                          value={estadoActual}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "no_aplica") {
                              handleMarcarNoAplica?.({ tipo_documento: tipo.value });
                            }
                          }}
                        >
                          {buildEstadoOptions(null).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      )}
                      {doc && esAntecedentes && (
                        <div className="mt-2">
                          <label className="form-label small text-muted mb-1">Resultado antecedentes</label>
                          <select
                            className="form-select form-select-sm"
                            value={resultadoAntecedentes || ""}
                            onChange={(e) =>
                              handleCambiarEstado?.(doc.id, estadoActual, {
                                resultado_antecedentes: e.target.value || null,
                              })
                            }
                          >
                            <option value="">Sin definir</option>
                            <option value="no">No tiene antecedentes</option>
                            <option value="si">Si tiene antecedentes</option>
                          </select>
                        </div>
                      )}
                    </td>
                    <td>
                      <OcrBadge
                        doc={doc && !isNoAplica ? doc : null}
                        onRetry={handleRetryAnalisis}
                        onVerDetalle={handleVerDetalleOcr}
                        retrying={retryingAnalisis}
                      />
                    </td>
                    <td>
                      <div className="d-flex justify-content-end gap-2">
                        {doc && !isNoAplica && (
                          <>
                            <a
                              className="btn btn-outline-primary btn-sm"
                              href={getVerUrl(doc.id)}
                              onClick={(e) => {
                                e.preventDefault();
                                setPreviewDoc(doc);
                              }}
                              title="Vista previa"
                              aria-label="Vista previa"
                            >
                              <i className="bi bi-eye" />
                            </a>
                            <a className="btn btn-outline-secondary btn-sm" href={getDescargarUrl(doc.id)}>
                              <i className="bi bi-download" />
                            </a>
                          </>
                        )}
                        {canUploadDoc && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={!seleccionado || uploading}
                            onClick={() => triggerUpload(tipo.value)}
                          >
                            {doc ? "Reemplazar" : "Subir"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <input
        type="file"
        accept={DOCUMENTOS_EXT_PERMITIDAS.join(",")}
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={onFileChange}
      />

      {docConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Eliminar documento</h5>
                <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setDocConfirm(null)} />
              </div>
              <div className="modal-body">
                Seguro que deseas eliminar <strong>{docConfirm.nombre}</strong>?
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDocConfirm(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    await handleEliminarDoc?.(docConfirm.id);
                    setDocConfirm(null);
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {ocrDetalleDoc && (
        <OcrDetalleModal
          doc={ocrDetalleDoc}
          onClose={() => setOcrDetalleDoc(null)}
          onRetry={handleRetryAnalisis}
          retrying={retryingAnalisis}
          onLoad={handleVerAnalisis}
        />
      )}

      {previewDoc && (
        <div
          className="modal fade show d-block management-modal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          aria-modal="true"
          role="dialog"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: "960px" }} role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <div>
                  <h5 className="modal-title">Vista previa</h5>
                  <small className="text-muted">{previewDoc.nombre || documentoLabel(previewDoc.tipo)}</small>
                </div>
                <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setPreviewDoc(null)} />
              </div>
              <div className="modal-body pt-0">
                {previewMeta.isPdf && (
                  <iframe
                    title={previewDoc.nombre || "Documento"}
                    src={previewUrl}
                    style={{ width: "100%", height: "70vh", border: "0" }}
                  />
                )}
                {previewMeta.isImage && (
                  <div className="d-flex justify-content-center">
                    <img
                      src={previewUrl}
                      alt={previewDoc.nombre || "Documento"}
                      style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                    />
                  </div>
                )}
                {!previewMeta.isPdf && !previewMeta.isImage && (
                  <div className="alert alert-warning mb-0">
                    No se puede previsualizar este formato. Usa descargar.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <a className="btn btn-outline-secondary" href={getDescargarUrl(previewDoc.id)}>
                  Descargar
                </a>
                <a className="btn btn-outline-primary" href={previewUrl} target="_blank" rel="noreferrer">
                  Abrir en nueva ventana
                </a>
                <button type="button" className="btn btn-outline-dark" onClick={() => setPreviewDoc(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
